"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import callApi from "@api/api";

import Table from "@components/Table";
import Image from "next/image";

import Shimmer from "@components/Shimmer";

import PurpleIcon from "@public/widget/svg/purple-cap.svg";
import OrangeIcon from "@public/widget/svg/orange-cap.svg";

import useWindowDimensions from "@utils/useWindowDimensions";
import { transformDataMap } from "./transformMap";
import { encodeToBase64 } from "@utils/encodeHelper";
import { generateWidgetUrl } from "@utils/widgetHelper";
import { Theme } from "@/constants/enums";

import styles from "./Leaderboard.module.scss";
import { customPropsMap } from "./customPropsMap";

interface GenericAdvancedWidgetProps {
    dataKey: string;
    seriesKey?: string;
}

const GenericAdvancedWidget: React.FC<GenericAdvancedWidgetProps> = (props) => {
    const { dataKey, seriesKey } = props;

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const theme = localStorage.getItem("theme") ?? Theme.LIGHT;

    const dimension = useWindowDimensions();
    const router = useRouter();

    const configFunction = customPropsMap[dataKey];

    const { columns, columnStyles, title } = configFunction
        ? configFunction(theme, dimension)
        : { columns: [], columnStyles: {}, title: "" };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await callApi({
                    url: generateWidgetUrl(dataKey, seriesKey),
                    method: "GET",
                });

                if (response.isError) {
                    throw new Error("Network response was not ok");
                }

                const transformer = transformDataMap[dataKey];

                if (!transformer) {
                    throw new Error(
                        `No transformer found for datakey: ${dataKey}`,
                    );
                }

                const transformedData = transformer(response, dimension);

                const result: any = transformedData?.map((most_run_player) => ({
                    ...most_run_player,
                    batter: most_run_player.batter !== "-" && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <div
                                style={{
                                    borderBottom: "1px solid #D1D5DB",
                                    cursor: "pointer",
                                    // display: 'flex',
                                }}
                                className={styles["playerName"]}
                                data-theme={theme}
                                onClick={() => {
                                    router.push(
                                        `/wiki/sports/cricket/players/${encodeToBase64(
                                            `{"player_id":"${most_run_player.id}"}`,
                                        )}`,
                                    );
                                }}
                                title={`${most_run_player.batter}${
                                    most_run_player.shortSkill
                                        ? ` (${most_run_player.shortSkill})`
                                        : ""
                                }`}
                            >
                                {most_run_player?.batter}
                                {most_run_player?.shortSkill && (
                                    <span
                                        className={styles["playerStyleSpan"]}
                                        data-theme={theme}
                                    >
                                        ({most_run_player?.shortSkill})
                                    </span>
                                )}
                            </div>

                            {most_run_player.purplePlayerOrNot && (
                                <PurpleIcon />
                            )}
                            {most_run_player.OrangePlayerOrNot && (
                                <OrangeIcon />
                            )}
                        </div>
                    ),
                    team: most_run_player?.team && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "3px",
                                justifyContent: "center",
                            }}
                        >
                            {most_run_player?.teamFlag && (
                                <Image
                                    src={most_run_player?.teamFlag}
                                    alt={most_run_player?.team}
                                    width={10}
                                    height={10}
                                    style={{
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "1.55px",
                                    }}
                                />
                            )}
                            <p> {most_run_player?.team}</p>
                        </div>
                    ),
                }));

                setData(result);
            } catch (err: any) {
                setError(err.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dataKey, seriesKey]);

    if (loading) {
        return (
            <div style={{ width: "100%" }}>
                <Shimmer
                    theme={theme === Theme.DARK ? Theme.DARK : Theme.LIGHT}
                    className={styles["shimmer"]}
                    width="100%"
                    height="15vh"
                />
            </div>
        );
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div
            style={{
                background:
                    dimension?.width > 640
                        ? "white"
                        : theme === Theme.DARK
                        ? "black"
                        : "white",
                borderRadius: "8px",
                display: data?.length > 0 ? "" : "none",
                flex: 1,
            }}
        >
            <section className={styles["container-wrapper"]} data-theme={theme}>
                <div className={styles["header"]} data-theme={theme}>
                    {title}
                </div>
                <div
                    className={styles["table-wrapper-scroll"]}
                    data-theme={theme}
                >
                    <Table
                        columns={columns}
                        data={data}
                        tableClassName={styles["custom-table"]}
                        headerCellClassName={styles["header-cell"]}
                        tableDataClassName={styles["data-cell"]}
                        tableBodyClassName={styles["table-body-custom"]}
                        tableHeadClassName={styles["table-head"]}
                        tableRowClassName={styles["table-row"]}
                        columnStyles={columnStyles}
                        headerCellStyles={{
                            batter: {
                                color: "#4B5563",
                            },
                            runs: {
                                color: "#7A8190",
                            },
                            matches: {
                                color: "#7A8190",
                            },
                            team: {
                                color: "#7A8190",
                            },
                            average: {
                                color: "#7A8190",
                            },
                            strike: {
                                color: "#7A8190",
                            },
                            wickets: {
                                color: "#7A8190",
                            },
                            fifties: {
                                color: "7A8190",
                            },
                            hundreds: {
                                color: "7A8190",
                            },
                            economy: {
                                color: "7A8190",
                            },
                            highestIndividual: {
                                color: "7A8190",
                            },
                            fiveWickets: {
                                color: "7A8190",
                            },
                            catches: {
                                color: "7A8190",
                            },
                            dismissals: {
                                color: "7A8190",
                            },
                            BBI: {
                                color: "7A8190",
                            },
                            maidens: {
                                color: "7A8190",
                            },
                        }}
                        dataTheme={
                            dimension?.width > 640
                                ? Theme.LIGHT
                                : theme === Theme.DARK
                                ? Theme.DARK
                                : Theme.LIGHT
                        }
                    />
                </div>
            </section>
        </div>
    );
};

export default GenericAdvancedWidget;
